import React, { useEffect, useState } from "react";
import {
  deleteRajTransaction,
  getRajTransaction,
} from "../../../Rajasthan/Utility/RestApi";
import Loading from "../../../../components/Loading";

// Types
interface ContractData {
  id: number;
  start_date: string;
  end_date: string;
  withdrawal_loss: number;
  loi_number: number;
  loi_date: string;
  schedule_in: string;
  type_of_transaction: string;
  gna_number: number;
  trader__name: string;
  entity__name: string | number;
  gna_contract_date_from: string;
  gna_contract_date_to: string;
  gna_contract_time_from: string;
  gna_contract_time_to: string;
  ppa_contract_capacity: number;
  ppa_contract_capacity_lu_per_day: string;
  rate: number;
  gna_contract_created: number;
  ruvitl_scheduling_request: number;
}
// Header Cell Component
const HeaderCell: React.FC<{ title: string }> = ({ title }) => (
  <th className="py-3 text-[14px] px-4 border-b bg-[#FAFAFA] text-center font-medium text-[#6F6D7C] whitespace-nowrap">
    {title}
  </th>
);

// Data Cell Component
const DataCell: React.FC<{ value: string | number; className?: string }> = ({
  value,
  className,
}) => (
  <td
    className={`py-2 px-4 text-[12px] border-b text-[#8C8A96] ${className} ${
      typeof value == "number" ? "font-semibold text-[#34656D]" : ""
    }  `}
  >
    {value}
  </td>
);

// Data Cell Component
const EditCell: React.FC<{
  value: string | number;
  className?: string;
  setDataTab: any;
  contract: ContractData;
  setContracts: any;
}> = ({ value, className, setContracts, contract, setDataTab }) => {
  const [loading, setLoading] = useState(false);

  async function HandleDelete(id: any) {
    setLoading(true);
    try {
      await deleteRajTransaction(id);
      const entity = await getRajTransaction();
      setContracts(entity.reverse());
      alert("Contract Deleted Successfully");

      setLoading(false);
    } catch (error) {
      alert("Error in deleting the contract");
    }
  }

  const handleEdit = () => {
    // Store the contract data in localStorage to access it on the form page
    localStorage.setItem("editContractData", JSON.stringify(contract));
    // Navigate to the form page with edit mode parameter
    setDataTab({ label: "Edit", value: "edit" });
  };

  return (
    <td
      className={`py-3 px-4 text-[12px] border-b text-[#8C8A96] flex items-center gap-5 h-full`}
    >
      <button
        onClick={handleEdit}
        type="button"
        className="text-[#F1935C] hover:text-[#F1935C] focus:outline-none"
      >
        <img src={"/edit.svg"} alt="" />
      </button>
      <button
        disabled={loading}
        onClick={() => HandleDelete(value)}
        type="button"
        className="text-[#ce3737] hover:text-[#ce3737] focus:outline-none"
      >
        <img width={16} src={"/trash.svg"} alt="" />
      </button>
    </td>
  );
};

// Contract Row Component
const ContractRow: React.FC<{
  contract: ContractData;
  setDataTab: any;
  setContracts: React.Dispatch<React.SetStateAction<ContractData[]>>;
}> = ({ contract, setContracts, setDataTab }) => (
  <tr className="hover:bg-gray-50">
    <DataCell value={contract.start_date} />
    <DataCell value={contract.end_date} />
    <DataCell value={contract.withdrawal_loss} />
    <DataCell value={contract.loi_number} />
    <DataCell value={contract.loi_date} />
    <DataCell value={contract.schedule_in} />
    <DataCell value={contract.type_of_transaction} />
    <DataCell className="text-[#F1935C]" value={contract.gna_number} />
    <DataCell value={contract.trader__name} />
    <DataCell value={contract.entity__name} />
    <DataCell value={contract.gna_contract_date_from} />
    <DataCell value={contract.gna_contract_date_to} />
    <DataCell value={contract.gna_contract_time_from} />
    <DataCell value={contract.gna_contract_time_to} />
    <DataCell value={contract.ppa_contract_capacity} />
    <DataCell value={contract.rate} />
    <DataCell value={contract.gna_contract_created} />
    <DataCell value={contract.ruvitl_scheduling_request} />
    <EditCell
      setDataTab={setDataTab}
      value={contract.id}
      contract={contract}
      setContracts={setContracts}
    />
  </tr>
);

// Main Component
const ContractTable: React.FC<{
  setDataTab: React.Dispatch<React.SetStateAction<any>>;
}> = ({ setDataTab }) => {
  const [loadingFull, setLoadingFull] = useState(false);
  const [contracts, setContracts] = useState<ContractData[]>([]);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");

  async function handleFetch() {
    setLoadingFull(true);
    const entity = await getRajTransaction();
    setContracts([]);
    setLoadingFull(false);
  }

  useEffect(() => {
    handleFetch();
  }, []);

  // Filter contracts based on search term
  const filteredContracts = contracts.filter((contract) =>
    Object.values(contract).some((value) =>
      String(value).toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  // Get current contracts
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentContracts = filteredContracts
    .reverse()
    .slice(indexOfFirstItem, indexOfLastItem);

  // Change page
  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);

  // Calculate total pages
  const totalPages = Math.ceil(filteredContracts.length / itemsPerPage);

  return (
    <>
      <>{loadingFull && <Loading />}</>

      {/* Search and pagination controls */}
      <div className="flex justify-between items-center p-4">
        <div className="flex items-center">
          <div className="relative mr-4">
            <input
              type="text"
              placeholder="Search..."
              className="pl-8 pr-4 py-2 border rounded-md"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1); // Reset to first page on search
              }}
            />
            <svg
              className="w-4 h-4 absolute left-2.5 top-3 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
          <select
            className="border rounded-md py-2 px-3"
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(Number(e.target.value));
              setCurrentPage(1); // Reset to first page when changing items per page
            }}
          >
            <option value={10}>10 per page</option>
            <option value={20}>20 per page</option>
            <option value={50}>50 per page</option>
            <option value={100}>100 per page</option>
          </select>
        </div>

        <button
          className="bg-[#F1935C] text-white rounded-md px-4 py-2 flex items-center gap-2"
          onClick={() => setDataTab({ label: "Add", value: "form" })}
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 4v16m8-8H4"
            />
          </svg>
          Add Contract
        </button>
      </div>

      <div
        className="contract-table-container  h-[calc(100vh-350px)] overflow-y-auto"
        style={{
          scrollbarWidth: "auto", // For Firefox
          scrollbarColor: "#d1d5db transparent", // For Firefox
        }}
      >
        <style>{`
        /* For Chrome, Safari, and newer Edge */
        div::-webkit-scrollbar {
          height: 8px;
          width: 8px;
        }
        
        div::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }
        
        div::-webkit-scrollbar-thumb {
          background: #d1d5db;
          border-radius: 10px;
        }
        
        div::-webkit-scrollbar-thumb:hover {
          background: #9ca3af;
        }
      `}</style>
        <table className="table-auto text-nowrap w-full">
          <thead>
            <tr>
              {/* Basic Contract Details */}
              <HeaderCell title="Start Date" />
              <HeaderCell title="End Date" />
              <HeaderCell title="Withdrawal Loss (%)" />
              <HeaderCell title="LOI Number" />
              <HeaderCell title="LOI Date" />
              <HeaderCell title="Schedule In" />
              <HeaderCell title="Type of Transaction" />
              <HeaderCell title="GNA Number" />
              <HeaderCell title="Trader" />
              <HeaderCell title="Entity" />

              {/* GNA Contract Details */}
              <HeaderCell title="Contract Date From" />
              <HeaderCell title="Contract Date To" />
              <HeaderCell title="Contract Time From" />
              <HeaderCell title="Contract Time To" />

              {/* PPA Contract Details */}
              <HeaderCell title="Contract Capacity" />

              {/* Financial Details */}
              <HeaderCell title="Rate" />
              <HeaderCell title="GNA Contract Created (mw)" />
              <HeaderCell title="RUVITL Request" />
              <HeaderCell title="Actions" />
            </tr>
          </thead>
          <tbody>
            {currentContracts.length > 0 ? (
              currentContracts.map((contract, index) => (
                <ContractRow
                  setDataTab={setDataTab}
                  key={index}
                  setContracts={setContracts}
                  contract={contract}
                />
              ))
            ) : (
              <tr>
                <td colSpan={19} className="text-center py-4 text-gray-500">
                  {loadingFull
                    ? "Loading..."
                    : searchTerm
                    ? "No matching contracts found"
                    : "No contracts available"}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex justify-between items-center px-4 py-3 bg-white">
        <div className="flex-1 flex justify-between sm:hidden">
          <button
            onClick={() => paginate(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white ${
              currentPage === 1
                ? "opacity-50 cursor-not-allowed"
                : "hover:bg-gray-50"
            }`}
          >
            Previous
          </button>
          <button
            onClick={() => paginate(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages || totalPages === 0}
            className={`ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white ${
              currentPage === totalPages || totalPages === 0
                ? "opacity-50 cursor-not-allowed"
                : "hover:bg-gray-50"
            }`}
          >
            Next
          </button>
        </div>

        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
          <div>
            <p className="text-sm text-gray-700">
              Showing{" "}
              <span className="font-medium">{indexOfFirstItem + 1}</span> to{" "}
              <span className="font-medium">
                {Math.min(indexOfLastItem, filteredContracts.length)}
              </span>{" "}
              of <span className="font-medium">{filteredContracts.length}</span>{" "}
              results
            </p>
          </div>

          <div>
            <nav
              className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
              aria-label="Pagination"
            >
              <button
                onClick={() => paginate(1)}
                disabled={currentPage === 1}
                className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 ${
                  currentPage === 1
                    ? "opacity-50 cursor-not-allowed"
                    : "hover:bg-gray-50"
                }`}
              >
                <span className="sr-only">First</span>
                <svg
                  className="h-5 w-5"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                  <path
                    fillRule="evenodd"
                    d="M8.707 5.293a1 1 0 010 1.414L5.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>

              <button
                onClick={() => paginate(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className={`relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 ${
                  currentPage === 1
                    ? "opacity-50 cursor-not-allowed"
                    : "hover:bg-gray-50"
                }`}
              >
                <span className="sr-only">Previous</span>
                <svg
                  className="h-5 w-5"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>

              {/* Page numbers */}
              {[...Array(totalPages).keys()].map((number) => {
                // Show current page and up to 2 pages before and after
                if (
                  number + 1 === 1 ||
                  number + 1 === totalPages ||
                  (number + 1 >= currentPage - 2 &&
                    number + 1 <= currentPage + 2)
                ) {
                  return (
                    <button
                      key={number}
                      onClick={() => paginate(number + 1)}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                        currentPage === number + 1
                          ? "z-10 bg-primary border-primary text-white"
                          : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                      }`}
                    >
                      {number + 1}
                    </button>
                  );
                } else if (
                  number + 1 === currentPage - 3 ||
                  number + 1 === currentPage + 3
                ) {
                  // Show ellipsis
                  return (
                    <span
                      key={number}
                      className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700"
                    >
                      ...
                    </span>
                  );
                }
                return null;
              })}

              <button
                onClick={() => paginate(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages || totalPages === 0}
                className={`relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 ${
                  currentPage === totalPages || totalPages === 0
                    ? "opacity-50 cursor-not-allowed"
                    : "hover:bg-gray-50"
                }`}
              >
                <span className="sr-only">Next</span>
                <svg
                  className="h-5 w-5"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>

              <button
                onClick={() => paginate(totalPages)}
                disabled={currentPage === totalPages || totalPages === 0}
                className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 ${
                  currentPage === totalPages || totalPages === 0
                    ? "opacity-50 cursor-not-allowed"
                    : "hover:bg-gray-50"
                }`}
              >
                <span className="sr-only">Last</span>
                <svg
                  className="h-5 w-5"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                    clipRule="evenodd"
                  />
                  <path
                    fillRule="evenodd"
                    d="M11.293 14.707a1 1 0 010-1.414L14.586 10l-3.293-3.293a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </nav>
          </div>
        </div>
      </div>
    </>
  );
};

export default ContractTable;
